import React, {Component} from 'react';
import {PlayArrow, Stop, RestartAlt, Edit, Add, ExpandMore, ExpandLess, Refresh, Router, Visibility} from '@mui/icons-material';
import {Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Tab, Tabs, TextField, Tooltip, Typography} from '@mui/material';
import {CellTower, AltRoute, Language, Warning} from '@mui/icons-material';
import {connect} from 'react-redux';

import {
  listOperators, createOperator, updateOperator,
  stopOperator, startOperator, restartOperator,
  getKannelStatus,
  listKannelBoxes, listBoxOperators
} from '../../actions/manageOperators';
import Table from '../../components/Table';
import AddOperator from './addOperator';
import EditOperator from './editOperator';
import TenantRouting from './tenantRouting';
import GlobalOverride from './globalOverride';
import KannelBoxes from './kannelBoxes';
import {Inbox as InboxIcon} from '@mui/icons-material';

const ConfigRow = ({label, value, kannelKey}) => (
  <div style={{display: 'flex', padding: '6px 0', borderBottom: '1px solid rgba(0,0,0,0.06)'}}>
    <div style={{flex: 1, fontSize: 13, color: 'rgba(0,0,0,0.6)'}}>{label}</div>
    <div style={{flex: 1, fontSize: 13, fontWeight: 500, fontFamily: 'monospace'}}>{String(value)}</div>
    <div style={{width: 180, fontSize: 11, color: 'rgba(0,0,0,0.35)', fontFamily: 'monospace'}}>{kannelKey}</div>
  </div>
);

const ConfigSection = ({title, children}) => (
  <div style={{marginBottom: 16}}>
    <Typography variant="subtitle2" sx={{fontWeight: 600, color: 'primary.main', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5}}>
      {title}
    </Typography>
    {children}
  </div>
);

class ManageOperators extends Component {
  constructor() {
    super();
    this.state = {
      operators: [],
      boxes: [],
      operatorBoxes: {}, // operator_id -> [{boxId, displayName}]
      showAddDialog: false,
      showEditDialog: false,
      showDetailDialog: false,
      showWait: false,
      showStopConfirm: false,
      stopConfirmText: '',
      stopTarget: null,
      showRestartConfirm: false,
      restartConfirmText: '',
      restartTarget: null,
      kannelExpanded: false,
      kannelStatusText: '',
      activeTab: 0
    };
    this.editData = null;
    this.detailData = null;
    this.listReq = true;
    this.createReq = false;
    this.updateReq = false;
    this.stopReq = false;
    this.startReq = false;
    this.restartReq = false;
    this.kannelReq = false;
    this.boxesReq = true;
    this.pendingBoxOps = 0; // outstanding listBoxOperators calls
  }

  componentDidMount() {
    this.props.listOperators();
    this.props.listKannelBoxes();
  }

  componentWillReceiveProps(nextProps) {
    if (this.listReq && nextProps.operatorsList && nextProps.operatorsList.success) {
      this.listReq = false;
      // instances: parallel SMPP binds; the server stores 0/absent as 1.
      const ops = (nextProps.operatorsList.data?.operators || []).map(op => ({...op, id: op.id, instances: parseInt(op.instances, 10) || 1}));
      this.setState({operators: ops});
      return;
    }
    if (this.createReq && nextProps.createOperatorRes) {
      this.createReq = false;
      if (nextProps.createOperatorRes.success) {
        this.listReq = true;
        this.props.listOperators();
        alert('Operator created successfully');
        this.setState({showAddDialog: false, showWait: false});
      } else {
        alert(nextProps.createOperatorRes.message || nextProps.createOperatorRes.error || 'Failed to create operator');
        this.setState({showWait: false});
      }
      return;
    }
    if (this.updateReq && nextProps.updateOperatorRes) {
      this.updateReq = false;
      if (nextProps.updateOperatorRes.success) {
        this.listReq = true;
        this.props.listOperators();
        alert('Operator updated successfully');
        this.setState({showEditDialog: false, showWait: false});
      } else {
        alert(nextProps.updateOperatorRes.message || nextProps.updateOperatorRes.error || 'Failed to update operator');
        this.setState({showWait: false});
      }
      return;
    }
    if (this.stopReq && nextProps.stopOperatorRes) {
      this.stopReq = false;
      if (nextProps.stopOperatorRes.success) {
        this.listReq = true;
        this.props.listOperators();
        alert('Operator stopped');
      } else {
        alert(nextProps.stopOperatorRes.message || nextProps.stopOperatorRes.error || 'Failed to stop operator');
      }
      this.setState({showWait: false});
      return;
    }
    if (this.startReq && nextProps.startOperatorRes) {
      this.startReq = false;
      if (nextProps.startOperatorRes.success) {
        this.listReq = true;
        this.props.listOperators();
        alert('Operator started');
      } else {
        alert(nextProps.startOperatorRes.message || nextProps.startOperatorRes.error || 'Failed to start operator');
      }
      this.setState({showWait: false});
      return;
    }
    if (this.restartReq && nextProps.restartOperatorRes) {
      this.restartReq = false;
      if (nextProps.restartOperatorRes.success) {
        alert('Operator restarted');
      } else {
        alert(nextProps.restartOperatorRes.message || nextProps.restartOperatorRes.error || 'Failed to restart operator');
      }
      this.setState({showWait: false});
      return;
    }
    if (this.kannelReq && nextProps.kannelStatusRes) {
      this.kannelReq = false;
      const d = nextProps.kannelStatusRes?.data || {};
      const statusText = d.status_text || d.statusText || '';
      this.setState({kannelExpanded: true, kannelStatusText: statusText, showWait: false});
      return;
    }
    if (this.boxesReq && nextProps.kannelBoxesList?.success) {
      this.boxesReq = false;
      const boxes = nextProps.kannelBoxesList.data?.boxes || [];
      this.setState({boxes});
      this.fetchBoxOperators(boxes);
      return;
    }
  }

  fetchBoxOperators = async (boxes) => {
    if (!boxes?.length) return;
    const Api = require('../../api').default;
    const {LIST_BOX_OPERATORS_URL} = require('../../lib/constant');
    try {
      const results = await Promise.all(
        boxes.map(b => {
          const id = b.boxId || b.box_id;
          return Api.get(LIST_BOX_OPERATORS_URL.replace('{boxId}', id))
            .then(res => ({box: b, ids: res?.data?.data?.operatorIds || res?.data?.data?.operator_ids || []}))
            .catch(() => ({box: b, ids: []}));
        })
      );
      const map = {};
      results.forEach(({box, ids}) => {
        const boxLabel = {
          boxId: box.boxId || box.box_id,
          displayName: box.displayName || box.display_name || box.boxId || box.box_id
        };
        ids.forEach(opId => {
          if (!map[opId]) map[opId] = [];
          map[opId].push(boxLabel);
        });
      });
      this.setState({operatorBoxes: map});
    } catch (_) {
      // tolerate fetch errors — column just shows nothing
    }
  };

  handleCreate = (params) => {
    this.createReq = true;
    this.setState({showWait: true});
    this.props.createOperator(params);
  }

  handleUpdate = (params) => {
    this.updateReq = true;
    this.setState({showWait: true});
    this.props.updateOperator(this.editData.id, params);
  }

  handleStop = (row) => {
    this.setState({showStopConfirm: true, stopTarget: row, stopConfirmText: ''});
  }

  confirmStop = () => {
    const row = this.state.stopTarget;
    if (!row) return;
    this.stopReq = true;
    this.setState({showStopConfirm: false, stopTarget: null, stopConfirmText: '', showWait: true});
    this.props.stopOperator(row.smsc_id || row.smscId);
  }

  handleStart = (row) => {
    if (!window.confirm(`Start operator "${row.name}" (${row.smsc_id || row.smscId})? Traffic will resume to this SMSC.`)) return;
    this.startReq = true;
    this.setState({showWait: true});
    this.props.startOperator(row.smsc_id || row.smscId);
  }

  handleRestart = (row) => {
    this.setState({showRestartConfirm: true, restartTarget: row, restartConfirmText: ''});
  }

  confirmRestart = () => {
    const row = this.state.restartTarget;
    if (!row) return;
    this.restartReq = true;
    this.setState({showRestartConfirm: false, restartTarget: null, restartConfirmText: '', showWait: true});
    this.props.restartOperator(row.smsc_id || row.smscId);
  }

  handleKannelStatus = () => {
    this.kannelReq = true;
    this.setState({showWait: true});
    this.props.getKannelStatus();
  }

  handleKannelRefresh = () => {
    this.kannelReq = true;
    this.props.getKannelStatus();
  }

  openEdit = (row) => {
    this.editData = row;
    this.setState({showEditDialog: true});
  }

  openDetail = (row) => {
    this.detailData = row;
    this.setState({showDetailDialog: true});
  }

  renderOperatorsTab() {
    const {operators, showAddDialog, showEditDialog, showWait} = this.state;

    return (
      <div>
        <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16}}>
          <div>
            <p style={{fontSize: 16, fontWeight: 500, margin: 0}}>Operators ({operators.length})</p>
            <p style={{fontSize: 12, color: 'rgba(0,0,0,0.5)', margin: 0}}>
              Active: {operators.filter(o => o.is_active || o.isActive).length} |
              Inactive: {operators.filter(o => !(o.is_active || o.isActive)).length}
            </p>
          </div>
          <div style={{marginLeft: 'auto', display: 'flex', gap: 8}}>
            <Button onClick={() => this.setState({showAddDialog: true})} variant='contained' color='primary' startIcon={<Add />}>
              Add Operator
            </Button>
          </div>
        </div>

          <Table
            column={[
              {name: 'Code', value: 'code'},
              {name: 'Name', value: 'name'},
              {name: 'SMSC ID', value: 'smsc_id', cell: (val, row) => val || row.smscId},
              {name: 'Protocol', value: 'protocol'},
              {name: 'Host:Port', value: 'host', cell: (val, row) => `${val}:${row.port}`},
              {name: 'TPS / session', value: 'throughput'},
              {name: 'Sessions', value: 'instances', cell: (val, row) => {
                // TPS is per session; the total is derived for display only.
                const n = parseInt(val, 10) || 1;
                const tps = parseInt(row.throughput, 10) || 0;
                return n > 1 ? `${n} (${n * tps} total)` : String(n);
              }},
              {name: 'DLT TM ID', value: 'dltTmId', cell: (val, row) => {
                const tmId = val || row.dlt_tm_id || '';
                const mode = row.dltTmMode || row.dlt_tm_mode || '';
                if (!tmId) {
                  // No operator-level TM ID — the chain is assembled from the
                  // entity + ComBird TM-A only (test SMSCs run like this).
                  return (
                    <Tooltip title="No operator TM ID set — TLV 0x1402 chain carries entity + ComBird TM-A only">
                      <Chip label="inherit" size="small" variant="outlined" sx={{color: 'rgba(0,0,0,0.45)'}} />
                    </Tooltip>
                  );
                }
                return (
                  <Tooltip title={`TLV 0x1402 mode: ${mode || 'chain'}`}>
                    <span style={{fontFamily: 'monospace', fontSize: 12}}>{tmId}</span>
                  </Tooltip>
                );
              }},
              {name: 'Status', value: 'is_active', cell: (val, row) => {
                const active = val !== undefined ? val : row.isActive;
                return <Chip
                  label={active ? 'Active' : 'Inactive'}
                  color={active ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                />;
              }},
              {name: 'Default', value: 'is_default', cell: (val, row) => {
                const def = val !== undefined ? val : row.isDefault;
                return def ? <Chip label="Default" color="primary" size="small" /> : null;
              }},
              {name: 'Box', value: 'id', cell: (val, row) => {
                const boxes = this.state.operatorBoxes[row.id] || [];
                if (!boxes.length) return <span style={{color: 'rgba(0,0,0,0.35)', fontSize: 12}}>—</span>;
                return (
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: 4}}>
                    {boxes.map(b => (
                      <Chip
                        key={b.boxId}
                        label={b.displayName}
                        size="small"
                        color={b.boxId === 'default' ? 'default' : 'secondary'}
                        variant="outlined"
                      />
                    ))}
                  </div>
                );
              }},
              {name: 'Actions', value: 'id', style: {width: 190}, cell: (val, row) => {
                const active = row.is_active !== undefined ? row.is_active : row.isActive;
                return (
                  <div style={{display: 'flex', gap: 2}}>
                    <Tooltip title="View Config">
                      <IconButton size="small" onClick={(e) => {e.stopPropagation(); this.openDetail(row);}}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={(e) => {e.stopPropagation(); this.openEdit(row);}}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {active ?
                      <Tooltip title="Stop">
                        <IconButton size="small" color="error" onClick={(e) => {e.stopPropagation(); this.handleStop(row);}}>
                          <Stop fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      :
                      <Tooltip title="Start">
                        <IconButton size="small" color="success" onClick={(e) => {e.stopPropagation(); this.handleStart(row);}}>
                          <PlayArrow fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                    <Tooltip title="Restart">
                      <IconButton size="small" onClick={(e) => {e.stopPropagation(); this.handleRestart(row);}}>
                        <RestartAlt fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                );
              }}
            ]}
            serialNumber
            data={operators}
            showPagination
            pageOptions={[25, 50, 100]}
            defaultPageSize={50}
          />

          {/* Kannel Gateway Panel */}
          <Paper
            variant="outlined"
            sx={{mt: 2, borderRadius: 2, overflow: 'hidden', borderColor: this.state.kannelExpanded ? 'primary.main' : 'divider'}}
          >
            <div
              onClick={() => {
                if (!this.state.kannelExpanded && !this.state.kannelStatusText) {
                  this.handleKannelStatus();
                } else {
                  this.setState(prev => ({kannelExpanded: !prev.kannelExpanded}));
                }
              }}
              style={{
                display: 'flex', alignItems: 'center', padding: '12px 16px',
                cursor: 'pointer', userSelect: 'none',
                background: this.state.kannelExpanded ? 'rgba(25, 118, 210, 0.04)' : 'transparent'
              }}
            >
              <Router sx={{mr: 1.5, color: 'primary.main'}} />
              <Typography variant="subtitle1" sx={{fontWeight: 600, flex: 1}}>
                Kannel Gateway
              </Typography>
              {this.state.kannelExpanded &&
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={(e) => {e.stopPropagation(); this.handleKannelRefresh();}} sx={{mr: 1}}>
                    <Refresh fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
              {this.state.kannelExpanded ? <ExpandLess /> : <ExpandMore />}
            </div>
            <Collapse in={this.state.kannelExpanded}>
              <div style={{padding: '0 16px 16px'}}>
                {this.state.kannelStatusText ? (
                  <pre style={{
                    margin: 0, padding: 12, background: '#1e1e1e', color: '#d4d4d4',
                    borderRadius: 8, fontSize: 12, lineHeight: 1.5,
                    maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word'
                  }}>
                    {this.state.kannelStatusText}
                  </pre>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{py: 2, textAlign: 'center'}}>
                    Loading Kannel status...
                  </Typography>
                )}
              </div>
            </Collapse>
          </Paper>

        {showAddDialog &&
          <AddOperator
            onSubmit={this.handleCreate}
            onClose={() => this.setState({showAddDialog: false})}
          />
        }

        {showEditDialog && this.editData &&
          <EditOperator
            data={this.editData}
            onSubmit={this.handleUpdate}
            onClose={() => {this.editData = null; this.setState({showEditDialog: false});}}
          />
        }

        {this.state.showDetailDialog && this.detailData && (() => {
          const d = this.detailData;
          const g = (snake, camel) => d[snake] !== undefined ? d[snake] : (d[camel] !== undefined ? d[camel] : '-');
          const isSmpp = (d.protocol || 'smpp') === 'smpp';
          const tonLabel = (v) => ({0:'Unknown',1:'International',2:'National',3:'Network Specific',5:'Alphanumeric',6:'Abbreviated'}[v] || v);
          const npiLabel = (v) => ({0:'Unknown',1:'ISDN/E.164',3:'Data/X.121',4:'Telex',8:'National',9:'Private',14:'Internet'}[v] || v);
          const versionLabel = (v) => ({34:'SMPP 3.4',50:'SMPP 5.0',33:'SMPP 3.3'}[v] || v);
          // TPS is per session, stored as entered; the total is derived for display only.
          const sessions = parseInt(d.instances, 10) || 1;
          const perSessionTps = parseInt(d.throughput, 10) || 0;
          return (
            <Dialog open={true} onClose={() => {this.detailData = null; this.setState({showDetailDialog: false});}} maxWidth="sm" fullWidth>
              <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Router color="primary" /> {d.name} Configuration
              </DialogTitle>
              <DialogContent dividers>
                <div style={{display: 'flex', padding: '4px 0 8px', borderBottom: '1px solid rgba(0,0,0,0.12)', marginBottom: 12}}>
                  <div style={{flex: 1, fontSize: 11, fontWeight: 600, color: 'rgba(0,0,0,0.4)', textTransform: 'uppercase'}}>Parameter</div>
                  <div style={{flex: 1, fontSize: 11, fontWeight: 600, color: 'rgba(0,0,0,0.4)', textTransform: 'uppercase'}}>Value</div>
                  <div style={{width: 180, fontSize: 11, fontWeight: 600, color: 'rgba(0,0,0,0.4)', textTransform: 'uppercase'}}>Kannel Key</div>
                </div>

                <ConfigSection title="Connection">
                  <ConfigRow label="Code" value={d.code} kannelKey="smsc-id" />
                  <ConfigRow label="SMSC ID" value={g('smsc_id','smscId')} kannelKey="smsc-id" />
                  <ConfigRow label="Protocol" value={d.protocol || 'smpp'} kannelKey="smsc" />
                  <ConfigRow label="Host" value={d.host} kannelKey="host" />
                  <ConfigRow label="Port" value={d.port} kannelKey="port" />
                  <ConfigRow label="Username" value={d.username || '(none)'} kannelKey="smsc-username" />
                  <ConfigRow label="System Type" value={g('system_type','systemType') || '(empty)'} kannelKey="system-type" />
                </ConfigSection>

                <ConfigSection title="Performance">
                  <ConfigRow label="Throughput (TPS)" value={d.throughput || 0} kannelKey="throughput" />
                  <ConfigRow label="Max Pending" value={g('max_pending','maxPending') || 5000} kannelKey="max-pending-submits" />
                  <ConfigRow label="Sessions (SMPP binds)" value={sessions} kannelKey="group = smsc (x N)" />
                  <ConfigRow label="Total TPS (derived)" value={`${sessions * perSessionTps} (${sessions} \u00d7 ${perSessionTps})`} kannelKey="sessions x throughput" />
                </ConfigSection>

                {isSmpp && <>
                  <ConfigSection title="SMPP Protocol">
                    <ConfigRow label="Interface Version" value={versionLabel(g('interface_version','interfaceVersion'))} kannelKey="interface-version" />
                    <ConfigRow label="Transceiver Mode" value={g('transceiver_mode','transceiverMode') !== false ? 'true' : 'false'} kannelKey="transceiver-mode" />
                  </ConfigSection>

                  <ConfigSection title="Address Configuration">
                    <ConfigRow label="Source Addr TON" value={`${g('source_addr_ton','sourceAddrTon')} (${tonLabel(g('source_addr_ton','sourceAddrTon'))})`} kannelKey="source-addr-ton" />
                    <ConfigRow label="Source Addr NPI" value={`${g('source_addr_npi','sourceAddrNpi')} (${npiLabel(g('source_addr_npi','sourceAddrNpi'))})`} kannelKey="source-addr-npi" />
                    <ConfigRow label="Dest Addr TON" value={`${g('dest_addr_ton','destAddrTon')} (${tonLabel(g('dest_addr_ton','destAddrTon'))})`} kannelKey="dest-addr-ton" />
                    <ConfigRow label="Dest Addr NPI" value={`${g('dest_addr_npi','destAddrNpi')} (${npiLabel(g('dest_addr_npi','destAddrNpi'))})`} kannelKey="dest-addr-npi" />
                  </ConfigSection>

                  <ConfigSection title="Connection Timers">
                    <ConfigRow label="Reconnect Delay" value={`${g('reconnect_delay','reconnectDelay') || 5}s`} kannelKey="reconnect-delay" />
                    <ConfigRow label="Keepalive" value={`${g('keepalive','keepalive') || 60}s`} kannelKey="keepalive" />
                    <ConfigRow label="Enquire Link Interval" value={`${g('enquire_link_interval','enquireLinkInterval') || 30}s`} kannelKey="enquire-link-interval" />
                    <ConfigRow label="Wait ACK" value={`${g('wait_ack','waitAck') || 10}s`} kannelKey="wait-ack" />
                    <ConfigRow label="Wait ACK Expire" value={g('wait_ack_expire','waitAckExpire') || '0x00'} kannelKey="wait-ack-expire" />
                  </ConfigSection>
                </>}

                <ConfigSection title="Status">
                  <ConfigRow label="Active" value={g('is_active','isActive') !== false ? 'Yes' : 'No'} kannelKey="-" />
                  <ConfigRow label="Default" value={g('is_default','isDefault') === true ? 'Yes' : 'No'} kannelKey="default-smsc" />
                  <ConfigRow label="Country Code" value={g('country_code','countryCode') || 'IN'} kannelKey="-" />
                </ConfigSection>

                <ConfigSection title="DLT Configuration">
                  <ConfigRow label="TM ID" value={d.dltTmId || d.dlt_tm_id || '(not set)'} kannelKey="TLV 0x1402" />
                  <ConfigRow label="TM Mode" value={d.dltTmMode || d.dlt_tm_mode || 'chain'} kannelKey="-" />
                  <ConfigRow label="Entity ID Tag" value={g('dlt_entity_tag','dltEntityTag') || '0x1400 (default)'} kannelKey="TLV tag" />
                  <ConfigRow label="Template ID Tag" value={g('dlt_template_tag','dltTemplateTag') || '0x1401 (default)'} kannelKey="TLV tag" />
                  <ConfigRow label="TM Chain Tag" value={g('dlt_tm_tag','dltTmTag') || '0x1402 (default)'} kannelKey="TLV tag" />
                </ConfigSection>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => {
                  this.detailData = null;
                  this.setState({showDetailDialog: false});
                }}>Close</Button>
                <Button variant="contained" onClick={() => {
                  this.editData = this.detailData;
                  this.detailData = null;
                  this.setState({showDetailDialog: false, showEditDialog: true});
                }}>Edit</Button>
              </DialogActions>
            </Dialog>
          );
        })()}

        {this.state.showStopConfirm && this.state.stopTarget &&
          <Dialog open={true} onClose={() => this.setState({showStopConfirm: false, stopTarget: null, stopConfirmText: ''})} maxWidth="sm" fullWidth>
            <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1, color: '#d32f2f'}}>
              <Warning color="error" /> Stop Operator
            </DialogTitle>
            <DialogContent>
              <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
                {this.state.stopTarget.name} ({this.state.stopTarget.smsc_id || this.state.stopTarget.smscId})
              </Typography>
              <Typography variant="body2" sx={{mb: 1, color: 'text.secondary'}}>
                This will immediately stop all SMS traffic flowing through this operator.
              </Typography>
              <Typography variant="body2" sx={{mb: 1}}>
                <b>What happens when you stop:</b>
              </Typography>
              <ul style={{margin: '0 0 16px 0', paddingLeft: 20, fontSize: 14, color: 'rgba(0,0,0,0.7)'}}>
                <li>All pending messages in the queue for this SMSC will be held</li>
                <li>No new messages will be routed to this operator</li>
                <li>The SMSC connection remains configured but inactive</li>
                <li>Use the Start button to resume traffic</li>
              </ul>
              <Typography variant="body2" sx={{mb: 1}}>
                Type <b>stop</b> to confirm:
              </Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder="Type 'stop' to confirm"
                value={this.state.stopConfirmText}
                onChange={(e) => this.setState({stopConfirmText: e.target.value})}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && this.state.stopConfirmText.toLowerCase() === 'stop') this.confirmStop();
                }}
                sx={{mt: 1}}
              />
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
              <Button onClick={() => this.setState({showStopConfirm: false, stopTarget: null, stopConfirmText: ''})}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={this.state.stopConfirmText.toLowerCase() !== 'stop'}
                onClick={this.confirmStop}
              >
                Stop Operator
              </Button>
            </DialogActions>
          </Dialog>
        }

        {this.state.showRestartConfirm && this.state.restartTarget &&
          <Dialog open={true} onClose={() => this.setState({showRestartConfirm: false, restartTarget: null, restartConfirmText: ''})} maxWidth="sm" fullWidth>
            <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1, color: '#ed6c02'}}>
              <Warning color="warning" /> Restart Operator
            </DialogTitle>
            <DialogContent>
              <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
                {this.state.restartTarget.name} ({this.state.restartTarget.smsc_id || this.state.restartTarget.smscId})
              </Typography>
              <Typography variant="body2" sx={{mb: 1, color: 'text.secondary'}}>
                This will disconnect and reconnect the SMSC connection.
              </Typography>
              <Typography variant="body2" sx={{mb: 1}}>
                <b>What happens when you restart:</b>
              </Typography>
              <ul style={{margin: '0 0 16px 0', paddingLeft: 20, fontSize: 14, color: 'rgba(0,0,0,0.7)'}}>
                <li>The SMSC connection will be dropped and re-established</li>
                <li>In-flight messages may be delayed or require retry</li>
                <li>Traffic resumes automatically once reconnected</li>
                <li>Use this after config changes or to fix connection issues</li>
              </ul>
              <Typography variant="body2" sx={{mb: 1}}>
                Type <b>restart</b> to confirm:
              </Typography>
              <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder="Type 'restart' to confirm"
                value={this.state.restartConfirmText}
                onChange={(e) => this.setState({restartConfirmText: e.target.value})}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && this.state.restartConfirmText.toLowerCase() === 'restart') this.confirmRestart();
                }}
                sx={{mt: 1}}
              />
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
              <Button onClick={() => this.setState({showRestartConfirm: false, restartTarget: null, restartConfirmText: ''})}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                disabled={this.state.restartConfirmText.toLowerCase() !== 'restart'}
                onClick={this.confirmRestart}
              >
                Restart Operator
              </Button>
            </DialogActions>
          </Dialog>
        }

        {showWait &&
          <Dialog open={true}>
            <DialogContent><CircularProgress /></DialogContent>
          </Dialog>
        }
      </div>
    );
  }

  render() {
    const isMobile = window.isMobile();
    const {activeTab} = this.state;

    return (
      <div className='sms-parent-container' style={isMobile ? {padding: 8} : {}}>
        <div className='sms-small-container'>
          <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 2}}>
            <Tabs
              value={activeTab}
              onChange={(e, val) => this.setState({activeTab: val})}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab icon={<CellTower />} iconPosition="start" label="SMSC Operators" />
              <Tab icon={<AltRoute />} iconPosition="start" label="Tenant Routing" />
              <Tab icon={<Language />} iconPosition="start" label="Global Override" />
              <Tab icon={<InboxIcon />} iconPosition="start" label="Kannel Boxes" />
            </Tabs>
          </Box>

          {activeTab === 0 && this.renderOperatorsTab()}
          {activeTab === 1 && <TenantRouting />}
          {activeTab === 2 && <GlobalOverride />}
          {activeTab === 3 && <KannelBoxes />}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    operatorsList: state.manageOperators.listOperatorsResponse,
    createOperatorRes: state.manageOperators.createOperatorResponse,
    updateOperatorRes: state.manageOperators.updateOperatorResponse,
    stopOperatorRes: state.manageOperators.stopOperatorResponse,
    startOperatorRes: state.manageOperators.startOperatorResponse,
    restartOperatorRes: state.manageOperators.restartOperatorResponse,
    kannelStatusRes: state.manageOperators.kannelStatusResponse,
    kannelBoxesList: state.manageOperators.listKannelBoxesResponse
  };
}

export default connect(mapStateToProps, {
  listOperators, createOperator, updateOperator,
  stopOperator, startOperator, restartOperator,
  getKannelStatus,
  listKannelBoxes, listBoxOperators
})(ManageOperators);
